import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { getSubcategoryLabel, resolveSubcategoryLabel } from '../constants/hubSubcategoryMaster';
import { businessInUserRegions } from '../lib/regionBusinessFilter';

function parseHubConfigOrder(data) {
  if (Array.isArray(data)) return data.map(String);
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseHubTags(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseHubAttributes(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function tagVisibleForCountry(row, countryCode) {
  if (row?.is_global !== false) return true;
  const spec = Array.isArray(row.country_specific) ? row.country_specific : [];
  if (!spec.length) return true;
  const cc = (countryCode || 'DEFAULT').trim().toUpperCase() || 'DEFAULT';
  return spec.includes(cc);
}

/**
 * 나라별 허브 필터칩 순서 조회
 * @param {string} category
 * @param {string} [countryCode]
 * @returns {Promise<string[]>}
 */
export async function getHubConfig(category, countryCode = 'DEFAULT') {
  if (!isSupabaseConfigured || !category) return [];
  const supabase = getSupabase();
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('get_hub_config', {
    p_category: category,
    p_country_code: countryCode || 'DEFAULT',
  });
  if (error) {
    console.error('[hub] getHubConfig error:', category, countryCode, error);
    return [];
  }
  const order = parseHubConfigOrder(data);
  console.log('[hub] getHubConfig', { category, countryCode, raw: data, order });
  return order;
}

/**
 * subcategory 선택 시 2단계 태그 목록
 * @param {string} category
 * @param {string} subcategory
 * @param {string} [countryCode]
 * @returns {Promise<Array<{tag:string,label_ko:string,label_en:string,label_vi?:string}>>}
 */
export async function getHubTags(category, subcategory, countryCode = 'DEFAULT') {
  if (!isSupabaseConfigured || !category || !subcategory) return [];
  const supabase = getSupabase();
  if (!supabase) return [];
  const cc = countryCode || 'DEFAULT';
  const { data, error } = await supabase.rpc('get_hub_tags', {
    p_category: category,
    p_subcategory: subcategory,
    p_country_code: cc,
  });
  if (!error) {
    return parseHubTags(data).filter((row) => tagVisibleForCountry(row, cc));
  }
  const { data: rows, error: tableErr } = await supabase
    .from('hub_tag_master')
    .select('tag, label_ko, label_en, label_vi, is_global, country_specific, sort_order')
    .eq('category', category)
    .eq('subcategory', subcategory)
    .order('sort_order', { ascending: true });
  if (tableErr) {
    console.error('getHubTags error:', error, tableErr);
    return [];
  }
  return (rows || []).filter((row) => tagVisibleForCountry(row, cc));
}

/**
 * @param {object} params
 * @param {string} params.category
 * @param {string|null} [params.subcategory]
 * @param {string|null} [params.tag]
 * @param {string|null} [params.countryCode]
 * @param {string|null} [params.cityCode] active region cityCode (e.g. DAD, SGN)
 * @param {string|null} [params.search]
 * @param {Record<string, boolean>|null} [params.attributes]
 * @param {string|null} [params.sort]
  /** @param {number|null} [params.userLat]
  * @param {number|null} [params.userLng]
  * @param {number|null} [params.radiusKm]
  * @param {number} [params.page]
  * @param {number} [params.limit]
  */
export async function getHubBusinesses({
  category,
  subcategory = null,
  tag = null,
  countryCode = null,
  cityCode = null,
  search = null,
  attributes = null,
  sort = null,
  userLat = null,
  userLng = null,
  radiusKm = null,
  page = 0,
  limit = 20,
}) {
  if (!isSupabaseConfigured || !category) return [];
  const supabase = getSupabase();
  if (!supabase) return [];
  const tagTrim = typeof tag === 'string' ? tag.trim() : '';
  const attrKeys = attributes && typeof attributes === 'object' ? Object.keys(attributes) : [];
  const hasAttrs = attrKeys.length > 0;
  const sortTrim = typeof sort === 'string' && sort.trim() ? sort.trim() : null;
  const hasCoords = Number.isFinite(Number(userLat)) && Number.isFinite(Number(userLng));
  const hasRadius = radiusKm != null && Number.isFinite(Number(radiusKm));

  const fullArgs = {
    p_category: category,
    p_subcategory: subcategory || null,
    p_tag: tagTrim || null,
    p_country_code: countryCode || null,
    p_search: search || null,
    p_attributes: hasAttrs ? attributes : null,
    p_sort: sortTrim,
    p_user_lat: hasCoords ? Number(userLat) : null,
    p_user_lng: hasCoords ? Number(userLng) : null,
    p_radius_km: hasRadius ? Number(radiusKm) : null,
    p_limit: limit,
    p_offset: page * limit,
  };

  let data;
  let error;
  ({ data, error } = await supabase.rpc('get_hub_businesses', fullArgs));

  if (error && (hasCoords || hasRadius)) {
    const noLocArgs = { ...fullArgs, p_user_lat: null, p_user_lng: null, p_radius_km: null };
    ({ data, error } = await supabase.rpc('get_hub_businesses', noLocArgs));
  }

  if (error && (hasAttrs || sortTrim)) {
    const midArgs = {
      p_category: category,
      p_subcategory: subcategory || null,
      p_tag: tagTrim || null,
      p_country_code: countryCode || null,
      p_search: search || null,
      p_limit: limit,
      p_offset: page * limit,
    };
    ({ data, error } = await supabase.rpc('get_hub_businesses', midArgs));
  }

  if (error) {
    const legacyArgs = {
      p_category: category,
      p_subcategory: subcategory || null,
      p_country_code: countryCode || null,
      p_search: search || null,
      p_limit: limit,
      p_offset: page * limit,
    };
    ({ data, error } = await supabase.rpc('get_hub_businesses', legacyArgs));
    if (!error && tagTrim) {
      data = (data || []).filter((b) => (Array.isArray(b.tags) ? b.tags : []).includes(tagTrim));
    }
  }

  if (error) {
    console.error('getHubBusinesses error:', error);
    return [];
  }
  let list = data || [];
  const code = String(cityCode || '')
    .trim()
    .toUpperCase();
  if (code) {
    list = list.filter((b) => businessInUserRegions(b, [code]));
  }
  return list;
}

/**
 * subcategory 마스터 라벨 조회
 * @param {string} category
 * @returns {Promise<Array>}
 */
export async function getSubcategoryMaster(category) {
  if (!isSupabaseConfigured || !category) return [];
  const supabase = getSupabase();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('hub_subcategory_master')
    .select('key, label_ko, label_en, label_vi, is_global, country_specific, sort_order')
    .eq('category', category)
    .order('sort_order', { ascending: true });
  if (error) {
    console.error('getSubcategoryMaster error:', error);
    return [];
  }
  return data || [];
}

/** @deprecated use getHubConfig */
export const fetchHubSubcategoryOrder = getHubConfig;

/** @deprecated use getSubcategoryMaster */
export async function fetchHubSubcategoryMaster(category, countryCode = 'DEFAULT') {
  const rows = await getSubcategoryMaster(category);
  const cc = (countryCode || 'DEFAULT').trim().toUpperCase() || 'DEFAULT';
  return rows.filter((row) => {
    if (row.is_global !== false) return true;
    const spec = Array.isArray(row.country_specific) ? row.country_specific : [];
    return spec.length === 0 || spec.includes(cc);
  });
}

/** @deprecated use getHubBusinesses */
export const fetchHubBusinesses = getHubBusinesses;

/**
 * 카테고리별 속성(3단계) 필터 목록
 * @param {string} category
 * @param {string} [countryCode]
 */
export async function getHubAttributes(category, countryCode = 'DEFAULT') {
  if (!isSupabaseConfigured || !category) return [];
  const supabase = getSupabase();
  if (!supabase) return [];
  const cc = countryCode || 'DEFAULT';
  const { data, error } = await supabase.rpc('get_hub_attributes', {
    p_category: category,
    p_country_code: cc,
  });
  if (!error) return parseHubAttributes(data);
  const { data: rows, error: tableErr } = await supabase
    .from('hub_attribute_config')
    .select('attribute_key, label_ko, label_en, label_vi, attr_type, attr_value, sort_order, display_priority, country_code, is_active')
    .eq('category', category)
    .in('country_code', [cc, 'DEFAULT'])
    .order('sort_order', { ascending: true });
  if (tableErr) {
    console.error('getHubAttributes error:', error, tableErr);
    return [];
  }
  const seen = new Set();
  const out = [];
  for (const row of rows || []) {
    if (row.is_active === false) continue;
    const key = String(row.attribute_key || '').trim();
    if (!key || seen.has(key)) continue;
    if (row.country_code === cc || !seen.has(key)) {
      seen.add(key);
      out.push({
        key,
        label_ko: row.label_ko,
        label_en: row.label_en,
        label_vi: row.label_vi,
        type: row.attr_type,
        value: row.attr_value,
        display_priority: row.display_priority ?? 0,
      });
    }
  }
  return out;
}

/**
 * hub_attribute_config/hub_tag_master only store ko/en/vi columns (no ru+).
 * ko is only ever shown to ko readers; every other locale falls back to en
 * (never ko) so non-Korean users never see leaked Korean text.
 * @param {{key:string,label_ko?:string,label_en?:string,label_vi?:string,type?:string,value?:string}} row
 */
export function resolveHubAttrLabel(row, lang = 'ko') {
  const l = String(lang || 'ko').toLowerCase();
  if (l === 'ko' || l.startsWith('ko-')) return row.label_ko || row.label_en || row.key;
  if (l.startsWith('vi')) return row.label_vi || row.label_en || row.key;
  if (l.startsWith('ru')) return row.label_ru || row.label_en || row.key;
  return row.label_en || row.label_ko || row.key;
}

/** @param {{tag:string,label_ko?:string,label_en?:string,label_vi?:string}} row */
export function resolveHubTagLabel(row, lang = 'ko') {
  const l = String(lang || 'ko').toLowerCase();
  if (l === 'ko' || l.startsWith('ko-')) return row.label_ko || row.label_en || row.tag;
  if (l.startsWith('vi')) return row.label_vi || row.label_en || row.tag;
  if (l.startsWith('ru')) return row.label_ru || row.label_en || row.tag;
  return row.label_en || row.label_ko || row.tag;
}

/**
 * @param {string} category
 * @param {string} lang
 * @param {string} [countryCode]
 */
export async function fetchHubFilterChips(category, lang = 'ko', countryCode = 'DEFAULT') {
  const [order, master] = await Promise.all([
    getHubConfig(category, countryCode),
    fetchHubSubcategoryMaster(category, countryCode),
  ]);
  const keys = order.length ? order : master.map((r) => r.key);
  const seen = new Set();
  const chips = [];
  for (const key of keys) {
    const k = String(key || '').trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    chips.push({
      key: k,
      label: resolveSubcategoryLabel(category, k, lang, master) || getSubcategoryLabel(k, lang),
    });
  }
  return chips;
}
